import {
  motion,
  useReducedMotion,
  type Variants,
  type HTMLMotionProps,
} from 'framer-motion';
import { forwardRef, type ElementRef } from 'react';

/**
 * Returns a motion-safe flag that honours `prefers-reduced-motion`.
 * Use this before applying any animation.
 */
export function useReducedMotionSafe(): boolean {
  return useReducedMotion() ?? false;
}

// ── Shared variant definitions ────────────────────────────────────────────────

export const panelVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, x: 40 },
  visible: (custom?: { delay?: number }) => ({
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { duration: 0.2, ease: 'easeOut', delay: custom?.delay ?? 0 },
  }),
  exit: {
    opacity: 0,
    scale: 0.96,
    x: 40,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
};

export const reducedPanelVariants: Variants = {
  hidden: { opacity: 0 },
  visible: (custom?: { delay?: number }) => ({
    opacity: 1,
    transition: { duration: 0.1, delay: custom?.delay ?? 0 },
  }),
  exit: { opacity: 0, transition: { duration: 0.08 } },
};

export const activityStripVariants: Variants = {
  idle: { backgroundColor: 'transparent' },
  flash: {
    backgroundColor: 'hsl(var(--primary) / 0.15)',
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

// ── Components ────────────────────────────────────────────────────────────────

type PanelMotionProps = HTMLMotionProps<'div'> & {
  children: React.ReactNode;
  className?: string;
  enterDelayMs?: number;
};

/**
 * Wraps a panel tile with enter/exit animation.
 * Automatically disables motion when `prefers-reduced-motion` is set.
 */
export const PanelMotion = forwardRef<ElementRef<typeof motion.div>, PanelMotionProps>(
  ({ children, className, enterDelayMs, ...props }, ref) => {
    const reduced = useReducedMotionSafe();
    const variants = reduced ? reducedPanelVariants : panelVariants;
    const delay = typeof enterDelayMs === 'number' ? Math.max(0, enterDelayMs) / 1000 : 0;

    return (
      <motion.div
        ref={ref}
        custom={{ delay }}
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className={className}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);
PanelMotion.displayName = 'PanelMotion';

type ActivityFlashProps = {
  active: boolean;
  children: React.ReactNode;
  className?: string;
};

/**
 * Briefly flashes the activity strip when a new agent action arrives.
 */
export function ActivityFlash({ active, children, className }: ActivityFlashProps) {
  const reduced = useReducedMotionSafe();

  return (
    <motion.div
      animate={reduced ? undefined : active ? 'flash' : 'idle'}
      variants={activityStripVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
}
